import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Lightning, FileText, MagnifyingGlass, Download, Copy, Calendar, Trash } from '@phosphor-icons/react';
import { formatDateTime } from '../lib/utils';

const STATUS_BADGES = {
  pending: { cls: 'bg-amber-100 text-amber-800', label: 'Pending' },
  in_progress: { cls: 'bg-blue-100 text-blue-800', label: 'In Progress' },
  completed: { cls: 'bg-emerald-100 text-emerald-800', label: 'Completed' },
  failed: { cls: 'bg-rose-100 text-rose-800', label: 'Failed' },
  refund_pending: { cls: 'bg-orange-100 text-orange-800', label: 'Refund Pending' },
  hold: { cls: 'bg-gray-200 text-gray-800', label: 'Hold' },
};

/* ╔════════════════════════════════════════════════════════════════════╗
 * ║ 1) ElectricityVendorUpload — vendor side: upload daily bill file ║
 * ╚════════════════════════════════════════════════════════════════════╝ */
export function ElectricityVendorUpload() {
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/electricity/files');
      setFiles(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="electricity-vendor-upload">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightning size={24} weight="duotone" className="text-amber-600" />
            Electricity — Daily Files (View-only)
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Admin team aapke liye files upload karti hai. Yahan sabhi uploaded files aur unki progress dikhegi.</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
        <b>Note:</b> Ab file upload aapke portal se nahi hota — humari admin team aapke liye files upload karti hai. Kuch bhi missing lage to admin ko batao.
        <br />Welcome <b>{user?.name}</b> · Login: {user?.email}
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="table-dense">
            <TableHeader><TableRow>
              <TableHead>Filename</TableHead><TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Bills</TableHead>
              <TableHead className="text-right">Total Amt</TableHead>
              <TableHead className="text-center">Pending</TableHead>
              <TableHead className="text-center">Done</TableHead>
              <TableHead className="text-center">Failed</TableHead>
              <TableHead>Assigned</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8">Loading…</TableCell></TableRow> :
               files.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Koi file upload nahi ki abhi tak</TableCell></TableRow> :
               files.map(f => (
                <TableRow key={f.id} data-testid={`vendor-file-${f.id}`}>
                  <TableCell className="text-xs font-medium">{f.filename}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(f.uploaded_at)}</TableCell>
                  <TableCell className="text-right text-sm font-bold">{f.total_rows}</TableCell>
                  <TableCell className="text-right text-sm font-bold text-emerald-700">₹{Math.round(f.total_amount).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-center"><span className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 font-bold">{f.pending_count}</span></TableCell>
                  <TableCell className="text-center"><span className="text-[10px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 font-bold">{f.completed_count}</span></TableCell>
                  <TableCell className="text-center"><span className={`text-[10px] rounded px-1.5 py-0.5 font-bold ${f.failed_count > 0 ? 'bg-rose-100 text-rose-800' : 'bg-gray-100 text-gray-500'}`}>{f.failed_count}</span></TableCell>
                  <TableCell className="text-xs">{f.assigned_to_name || <span className="text-gray-400 italic">unassigned</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


/* ╔════════════════════════════════════════════════════════════════════╗
 * ║ 2) ElectricityAdminFiles — admin manages uploaded files            ║
 * ╚════════════════════════════════════════════════════════════════════╝ */
export function ElectricityAdminFiles() {
  const [files, setFiles] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [assignStaff, setAssignStaff] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = vendorFilter ? { vendor_id: vendorFilter } : {};
      const { data } = await api.get('/electricity/files', { params });
      setFiles(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, [vendorFilter]);

  useEffect(() => {
    api.get('/electricity/electricity-vendors').then(({ data }) => setVendors(data || [])).catch(() => {});
    api.get('/users?role=staff').then(({ data }) => setStaffList(data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const assign = async () => {
    if (!assignStaff) return toast.error('Staff select karein');
    try {
      await api.post(`/electricity/files/${assignFor.id}/assign`, { staff_id: assignStaff });
      toast.success('✅ Assigned');
      setAssignFor(null); setAssignStaff('');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Assign fail'); }
  };

  const del = async (fid) => {
    if (!window.confirm('File aur saari bookings delete ho jayengi. Sure?')) return;
    try { await api.delete(`/electricity/files/${fid}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Delete fail'); }
  };

  const downloadReport = async (f) => {
    try {
      const res = await api.get(`/electricity/files/${f.id}/download-report`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Server already sends original filename in Content-Disposition; we use the same.
      let fname = f.filename || 'electricity_report.xlsx';
      if (!fname.toLowerCase().endsWith('.xlsx') && !fname.toLowerCase().endsWith('.xls')) fname += '.xlsx';
      else if (fname.toLowerCase().endsWith('.xls')) fname = fname.slice(0, -4) + '.xlsx';
      a.download = fname;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`📥 Downloaded: ${fname} — WhatsApp pe share kar do`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Download fail';
      toast.error(typeof detail === 'string' ? detail : 'Download fail');
    }
  };

  return (
    <div className="space-y-5" data-testid="electricity-admin-files">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Lightning size={24} weight="duotone" className="text-amber-600" />
            Electricity — Admin File Manager
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Vendor uploads ko staff ko assign karein. Auto status tracking.</p>
        </div>
        <select value={vendorFilter} onChange={e => setVendorFilter(e.target.value)} className="h-9 text-sm border rounded px-2" data-testid="ev-vendor-filter">
          <option value="">All vendors</option>
          {vendors.map(v => <option key={v.id} value={v.id}>{v.name} — {v.email}</option>)}
        </select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="table-dense">
            <TableHeader><TableRow>
              <TableHead>Vendor</TableHead><TableHead>Filename</TableHead><TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Bills</TableHead>
              <TableHead className="text-right">Total ₹</TableHead>
              <TableHead className="text-center">Pending</TableHead>
              <TableHead className="text-center">Done</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={9} className="text-center py-8">Loading…</TableCell></TableRow> :
               files.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-8 text-gray-500">Koi file nahi mili</TableCell></TableRow> :
               files.map(f => (
                <TableRow key={f.id} data-testid={`admin-file-${f.id}`}>
                  <TableCell className="text-xs font-bold">{f.vendor_name || '—'}</TableCell>
                  <TableCell className="text-xs">{f.filename}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(f.uploaded_at)}</TableCell>
                  <TableCell className="text-right text-sm font-bold">{f.total_rows}</TableCell>
                  <TableCell className="text-right text-sm font-bold text-emerald-700">₹{Math.round(f.total_amount).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-center"><span className="text-[10px] bg-amber-100 text-amber-800 rounded px-1.5 py-0.5 font-bold">{f.pending_count}</span></TableCell>
                  <TableCell className="text-center"><span className="text-[10px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5 font-bold">{f.completed_count}</span></TableCell>
                  <TableCell className="text-xs">{f.assigned_to_name || <span className="text-gray-400 italic">unassigned</span>}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadReport(f)} data-testid={`download-${f.id}`} title="Download Updated Excel for WhatsApp report">
                        <Download size={12} className="mr-1" /> Report
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setAssignFor(f); setAssignStaff(f.assigned_to_id || ''); }} data-testid={`assign-${f.id}`}>Assign</Button>
                      <button onClick={() => del(f.id)} className="p-1 text-rose-600 hover:bg-rose-50 rounded" title="Delete"><Trash size={14} /></button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {assignFor && (
        <Dialog open onOpenChange={v => !v && setAssignFor(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Assign file to staff</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
                <b>{assignFor.filename}</b> — {assignFor.total_rows} bills · ₹{Math.round(assignFor.total_amount).toLocaleString('en-IN')}
              </div>
              <select value={assignStaff} onChange={e => setAssignStaff(e.target.value)} className="w-full h-10 border rounded px-2 text-sm" data-testid="assign-staff-select">
                <option value="">— select staff —</option>
                {staffList.filter(s => ['staff', 'sub_staff'].includes(s.role)).map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
              <Button onClick={assign} disabled={!assignStaff} data-testid="assign-confirm">Assign</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


/* ╔════════════════════════════════════════════════════════════════════╗
 * ║ 3) ElectricityWorkspace — staff processes assigned bills           ║
 * ╚════════════════════════════════════════════════════════════════════╝ */
export function ElectricityWorkspace() {
  const [bookings, setBookings] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/electricity/my-queue', {
        params: { q: search || undefined, only_status: statusFilter || undefined },
      });
      setBookings(data || []);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const stats = useMemo(() => {
    const out = { pending: 0, in_progress: 0, completed: 0, failed: 0, total_amt: 0 };
    bookings.forEach(b => {
      out[b.status] = (out[b.status] || 0) + 1;
      out.total_amt += b.amount;
    });
    return out;
  }, [bookings]);

  const copy = (txt) => { navigator.clipboard.writeText(txt || ''); toast.success('Copied'); };

  return (
    <div className="space-y-4" data-testid="electricity-workspace">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Lightning size={24} weight="duotone" className="text-amber-600" />
          Electricity Workspace
        </h1>
        <div className="text-xs text-gray-500">{bookings.length} bills loaded · Total ₹{Math.round(stats.total_amt).toLocaleString('en-IN')}</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
        {[
          { k: 'pending', label: 'Pending', cls: 'bg-amber-50 text-amber-800 border-amber-200' },
          { k: 'in_progress', label: 'In Progress', cls: 'bg-blue-50 text-blue-800 border-blue-200' },
          { k: 'completed', label: 'Completed', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
          { k: 'failed', label: 'Failed', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
          { k: 'total_amt', label: 'Total ₹', cls: 'bg-violet-50 text-violet-800 border-violet-200' },
        ].map(s => (
          <button key={s.k} onClick={() => setStatusFilter(s.k === 'total_amt' ? '' : (statusFilter === s.k ? '' : s.k))}
            className={`p-2.5 rounded-lg border text-center transition-colors ${s.cls} ${statusFilter === s.k ? 'ring-2 ring-violet-400' : ''}`}>
            <div className="text-[10px] font-bold uppercase">{s.label}</div>
            <div className="text-lg font-bold">{s.k === 'total_amt' ? `₹${Math.round(stats.total_amt).toLocaleString('en-IN')}` : (stats[s.k] || 0)}</div>
          </button>
        ))}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search consumer no / TxnID / operator…" className="h-9 pl-8" data-testid="ew-search" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 text-sm border rounded px-2">
          <option value="">All status</option>
          {Object.keys(STATUS_BADGES).map(k => <option key={k} value={k}>{STATUS_BADGES[k].label}</option>)}
        </select>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="table-dense">
            <TableHeader><TableRow>
              <TableHead>Consumer No</TableHead><TableHead>Operator</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Amazon TxnID</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8">Loading…</TableCell></TableRow> :
               bookings.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Koi bill assigned nahi hai</TableCell></TableRow> :
               bookings.map(b => {
                const badge = STATUS_BADGES[b.status] || STATUS_BADGES.pending;
                return (
                  <TableRow key={b.id} data-testid={`ew-row-${b.id}`}>
                    <TableCell className="text-xs font-mono">
                      <button onClick={() => copy(b.consumer_no)} className="hover:text-violet-700 flex items-center gap-1">
                        {b.consumer_no} <Copy size={11} />
                      </button>
                    </TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate" title={b.operator}>{b.operator}</TableCell>
                    <TableCell className="text-right text-sm font-bold text-emerald-700">₹{Math.round(b.amount).toLocaleString('en-IN')}</TableCell>
                    <TableCell className="text-[10px] text-gray-600">{b.due_date ? new Date(b.due_date).toLocaleDateString('en-IN') : '-'}</TableCell>
                    <TableCell><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span></TableCell>
                    <TableCell className="text-[10px] font-mono">{b.amazon_txn_id || '-'}</TableCell>
                    <TableCell><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(b)} data-testid={`ew-process-${b.id}`}>Process</Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {editing && <ProcessDialog booking={editing} onClose={() => setEditing(null)} onDone={load} />}
    </div>
  );
}


function ProcessDialog({ booking, onClose, onDone }) {
  const [form, setForm] = useState({
    status: booking.status === 'pending' ? 'in_progress' : booking.status,
    amazon_txn_id: booking.amazon_txn_id || '',
    amazon_account: booking.amazon_account || '',
    card_used: booking.card_used || '',
    screenshot_url: booking.screenshot_url || '',
    remark: booking.remark || '',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/electricity/bookings/${booking.id}/process`, form);
      toast.success('✅ Saved');
      onClose(); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Save fail'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="process-dialog">
        <DialogHeader><DialogTitle>⚡ Process Bill</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2">
            <div><b>Consumer:</b> {booking.consumer_no}</div>
            <div><b>Operator:</b> {booking.operator}</div>
            <div><b>Amount:</b> ₹{Math.round(booking.amount).toLocaleString('en-IN')}</div>
            {booking.due_date && <div><b>Due:</b> {new Date(booking.due_date).toLocaleDateString('en-IN')}</div>}
          </div>
          <div>
            <label className="text-xs font-semibold">Status</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="w-full h-10 border rounded px-2 text-sm mt-1" data-testid="proc-status">
              {Object.keys(STATUS_BADGES).map(k => <option key={k} value={k}>{STATUS_BADGES[k].label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold">Amazon Transaction ID</label>
            <Input value={form.amazon_txn_id} onChange={e => setForm({ ...form, amazon_txn_id: e.target.value })} placeholder="Amazon order/txn ref" className="h-9 text-sm mt-1" data-testid="proc-amazon-txn" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">Amazon account</label>
              <Input value={form.amazon_account} onChange={e => setForm({ ...form, amazon_account: e.target.value })} placeholder="email" className="h-9 text-sm mt-1" data-testid="proc-amazon-acc" />
            </div>
            <div>
              <label className="text-xs font-semibold">Card used</label>
              <Input value={form.card_used} onChange={e => setForm({ ...form, card_used: e.target.value })} placeholder="last 4 / label" className="h-9 text-sm mt-1" data-testid="proc-card" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Screenshot URL (optional)</label>
            <Input value={form.screenshot_url} onChange={e => setForm({ ...form, screenshot_url: e.target.value })} placeholder="https://…" className="h-9 text-sm mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold">Remark</label>
            <Input value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })} placeholder="Notes…" className="h-9 text-sm mt-1" data-testid="proc-remark" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="proc-submit">{busy ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ╔════════════════════════════════════════════════════════════════════╗
 * ║ 4) ElectricityVendorLedger — vendor balance + commission payouts   ║
 * ╚════════════════════════════════════════════════════════════════════╝ */
export function ElectricityVendorLedger() {
  const { user } = useAuth();
  const isVendor = user?.role === 'electricity_vendor';
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState(isVendor ? (user?.id || '') : '');
  const [fromDate, setFromDate] = useState(monthStart.toISOString().slice(0, 10));
  const [toDate, setToDate] = useState(today.toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [showPayout, setShowPayout] = useState(false);

  useEffect(() => {
    if (!isVendor) {
      api.get('/electricity/electricity-vendors').then(({ data }) => {
        setVendors(data || []);
        if (data?.length && !vendorId) setVendorId(data[0].id);
      }).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor]);

  const load = useCallback(async () => {
    if (!vendorId) return;
    try {
      const { data } = await api.get(`/electricity/ledger/${vendorId}`, {
        params: { from_date: fromDate, to_date: toDate, limit: 1000 },
      });
      setData(data);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Load fail'); }
  }, [vendorId, fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data?.entries?.length) return toast.error('Koi entry nahi hai export ke liye');
    const rows = [['Date/Time', 'Type', 'Amount', 'Balance Change', 'Running Balance', 'Filename', 'Remark']];
    data.entries.forEach(e => rows.push([
      e.created_at ? formatDateTime(e.created_at) : '',
      e.type, e.amount, e.balance_change, e.running_balance,
      e.filename || '', e.remark || '',
    ]));
    const csv = rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `electricity_ledger_${vendorId}_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="electricity-ledger">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Lightning size={24} weight="duotone" className="text-amber-600" />
          Electricity Vendor Ledger
        </h1>
        <div className="flex gap-2">
          <Button onClick={exportCSV} variant="outline" size="sm" className="gap-1"><Download size={14} /> Export CSV</Button>
          {!isVendor && (
            <Button onClick={() => setShowPayout(true)} className="bg-violet-600 hover:bg-violet-700" size="sm">💰 Run Commission Payout</Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          {!isVendor && (
            <div>
              <label className="text-xs font-semibold">Vendor</label>
              <select value={vendorId} onChange={e => setVendorId(e.target.value)} className="w-full h-9 border rounded px-2 text-sm mt-1" data-testid="ledger-vendor">
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name} — {v.email}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-semibold flex items-center gap-1"><Calendar size={12} />From</label>
            <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold flex items-center gap-1"><Calendar size={12} />To</label>
            <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 mt-1" />
          </div>
          <Button onClick={load} className="self-end h-9">Refresh</Button>
        </CardContent>
      </Card>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Current Balance" val={`₹${Math.round(data.current_balance).toLocaleString('en-IN')}`} cls={data.current_balance >= 0 ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-rose-50 text-rose-800 border-rose-200'} />
            <StatCard label="Total Advances" val={`₹${Math.round(data.total_advances).toLocaleString('en-IN')}`} cls="bg-amber-50 text-amber-800 border-amber-200" />
            <StatCard label="Commission Paid" val={`₹${Math.round(data.total_commission_paid).toLocaleString('en-IN')}`} cls="bg-violet-50 text-violet-800 border-violet-200" />
            <StatCard label="Refunds" val={`₹${Math.round(data.total_refunds).toLocaleString('en-IN')}`} cls="bg-rose-50 text-rose-800 border-rose-200" />
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <Table className="table-dense">
                <TableHeader><TableRow>
                  <TableHead>Date/Time</TableHead><TableHead>Type</TableHead><TableHead>File</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Running Balance</TableHead>
                  <TableHead>Remark</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {data.entries.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Koi entry nahi mili</TableCell></TableRow> :
                   data.entries.map(e => (
                    <TableRow key={e.id}>
                      <TableCell className="text-[11px] whitespace-nowrap">{e.created_at ? formatDateTime(e.created_at) : '-'}</TableCell>
                      <TableCell><span className={`text-[10px] font-bold rounded px-1.5 py-0.5 ${e.type === 'advance_received' ? 'bg-emerald-100 text-emerald-800' : e.type === 'commission_payout' ? 'bg-violet-100 text-violet-800' : 'bg-rose-100 text-rose-800'}`}>{e.type.replace('_', ' ')}</span></TableCell>
                      <TableCell className="text-xs">{e.filename || '-'}</TableCell>
                      <TableCell className={`text-right text-xs font-bold ${e.balance_change >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{e.balance_change >= 0 ? '+' : ''}₹{Math.round(e.balance_change).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-xs font-bold">₹{Math.round(e.running_balance).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-[11px] text-gray-600">{e.remark || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      {showPayout && <PayoutDialog vendorId={vendorId} vendors={vendors} onClose={() => setShowPayout(false)} onDone={load} />}
    </div>
  );
}


function PayoutDialog({ vendorId, vendors, onClose, onDone }) {
  const today = new Date();
  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const [vid, setVid] = useState(vendorId);
  const [periodStart, setPeriodStart] = useState(lastMonthStart.toISOString().slice(0, 10));
  const [periodEnd, setPeriodEnd] = useState(lastMonthEnd.toISOString().slice(0, 10));
  const [rate, setRate] = useState(0.9);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/electricity/commission-payout', {
        vendor_id: vid, period_start: periodStart, period_end: periodEnd, custom_rate: parseFloat(rate),
      });
      toast.success(`✅ Payout: Base ₹${Math.round(data.base).toLocaleString('en-IN')} · Commission ₹${data.commission.toFixed(2)} + GST ₹${data.gst.toFixed(2)} = Total ₹${data.total_payout.toFixed(2)}`);
      onClose(); onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Payout fail'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>💰 Commission Payout</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold">Vendor</label>
            <select value={vid} onChange={e => setVid(e.target.value)} className="w-full h-9 border rounded px-2 text-sm mt-1">
              {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">Period from</label>
              <Input type="date" value={periodStart} onChange={e => setPeriodStart(e.target.value)} className="h-9 mt-1" />
            </div>
            <div>
              <label className="text-xs font-semibold">Period to</label>
              <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} className="h-9 mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold">Commission rate (%)</label>
            <Input type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} className="h-9 mt-1" />
          </div>
          <div className="text-[11px] bg-blue-50 border border-blue-200 rounded p-2 text-blue-900">
            Saari completed bookings ka total amount × {rate}% + 18% GST = total payout. Ledger me debit hoga.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-violet-600 hover:bg-violet-700">{busy ? 'Running…' : 'Payout'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function StatCard({ label, val, cls }) {
  return (
    <div className={`p-3 rounded-lg border ${cls} text-center`}>
      <div className="text-[10px] font-bold uppercase">{label}</div>
      <div className="text-xl font-bold mt-0.5">{val}</div>
    </div>
  );
}


export default ElectricityWorkspace;
